import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getStripeLanguage } from '../../../../i18n'
import { useAuth } from '../../../../hooks/useAuth'
import StripeCheckoutModal from '../../../../components/StripeCheckoutModal'
import { savePendingCheckout } from '../../../../utils/analytics'
import { savePendingUpsellSuccess } from './upsellCheckoutReturn'
import { persistActiveCampaignForReturn } from '../../utils/campaignRestore'
import { getPackagePriceValue } from './upsellCatalog'

const CLOSED_MODAL = {
  isOpen: false,
  checkoutType: 'subscription',
  tier: null,
  packageCode: null,
  promotionCode: null,
  campaignArtGenerationId: null,
  offerCode: null,
  upsellFlow: null,
  upsellSuccessStep: null,
  isDownloadCheckout: false,
  language: 'ptBR',
}

function parseCheckoutPrice(value) {
  if (typeof value === 'number') return value

  return (
    parseFloat(String(value || 0).replace(/[^\d,.-]/g, '').replace(',', '.')) || 0
  )
}

function buildReturnUrl({
  addonSuccess = false,
  downloadSuccess = false,
  subscriptionSuccess = false,
  upsellFlow = null,
  upsellSuccessStep = null,
  campaignArtGenerationId = null,
} = {}) {
  const url = new URL(window.location.href)

  if (addonSuccess) {
    url.searchParams.set('addonCheckoutSuccess', 'true')
  }

  if (downloadSuccess) {
    url.searchParams.set('downloadCheckoutSuccess', 'true')
  }

  if (subscriptionSuccess) {
    url.searchParams.set('subscriptionCheckoutSuccess', 'true')
  }

  if (upsellFlow && upsellSuccessStep) {
    url.searchParams.set('upsellFlow', upsellFlow)
    url.searchParams.set('upsellStep', upsellSuccessStep)
  }

  if (campaignArtGenerationId) {
    url.searchParams.set('campaignArtGenerationId', campaignArtGenerationId)
  }

  return url.toString()
}

export function resolvePlanCheckoutPayload(option, fallbackPlan) {
  const plan = option?.plan ?? fallbackPlan
  const tier = option?.tier ?? plan?.tier

  return {
    tier,
    name: plan?.description ?? option?.label ?? '',
    price: parseCheckoutPrice(plan?.price),
  }
}

export function resolvePackageCheckoutPayload(
  option,
  fallbackPackage,
  fallbackPackageCode = null,
) {
  const pkg = option?.package ?? option ?? fallbackPackage

  return {
    packageCode: pkg?.code ?? option?.code ?? fallbackPackageCode,
    name: pkg?.title ?? option?.label ?? '',
    price: getPackagePriceValue(pkg),
  }
}

export function useUpsellStripeCheckout() {
  const { userData } = useAuth()
  const { i18n } = useTranslation()
  const [modalConfig, setModalConfig] = useState(CLOSED_MODAL)

  const activeLanguage = modalConfig.isOpen
    ? modalConfig.language
    : (i18n.language || getStripeLanguage())
  const currency = activeLanguage === 'ptBR' ? 'BRL' : 'USD'

  const closeStripeCheckout = useCallback(() => {
    setModalConfig(CLOSED_MODAL)
  }, [])

  const openSubscriptionCheckout = useCallback(
    ({
      tier,
      name,
      price,
      campaignArtGenerationId,
      upsellFlow,
      upsellSuccessStep,
      offerCode,
      onOpen,
    } = {}) => {
      if (!tier) return

      if (campaignArtGenerationId) {
        persistActiveCampaignForReturn(campaignArtGenerationId)
      }

      savePendingCheckout({
        name: name || '',
        price: parseCheckoutPrice(price),
        currency,
        checkoutType: 'subscription',
      })

      savePendingUpsellSuccess({ flow: upsellFlow, step: upsellSuccessStep })

      setModalConfig({
        isOpen: true,
        checkoutType: 'subscription',
        tier,
        packageCode: null,
        promotionCode: null,
        offerCode: offerCode ?? null,
        campaignArtGenerationId: campaignArtGenerationId ?? null,
        upsellFlow: upsellFlow ?? null,
        upsellSuccessStep: upsellSuccessStep ?? null,
        isDownloadCheckout: false,
        language: getStripeLanguage(),
      })

      onOpen?.()
    },
    [currency],
  )

  const openAddonCheckout = useCallback(
    ({
      packageCode,
      name,
      price,
      promotionCode,
      offerCode,
      campaignArtGenerationId,
      upsellFlow,
      upsellSuccessStep,
      onOpen,
    } = {}) => {
      if (!packageCode) return

      const isDownloadCheckout = packageCode === 'addon_download'

      if (campaignArtGenerationId) {
        persistActiveCampaignForReturn(campaignArtGenerationId)
      }

      savePendingCheckout({
        name: name || '',
        price: parseCheckoutPrice(price),
        currency,
        checkoutType: 'addon',
        packageCode,
        ...(promotionCode ? { promotionCode } : {}),
        ...(offerCode ? { offerCode } : {}),
        ...(campaignArtGenerationId ? { campaignArtGenerationId } : {}),
      })

      savePendingUpsellSuccess({ flow: upsellFlow, step: upsellSuccessStep })

      setModalConfig({
        isOpen: true,
        checkoutType: 'addon',
        tier: null,
        packageCode,
        promotionCode: promotionCode ?? null,
        offerCode: offerCode ?? null,
        campaignArtGenerationId: campaignArtGenerationId ?? null,
        upsellFlow: upsellFlow ?? null,
        upsellSuccessStep: upsellSuccessStep ?? null,
        isDownloadCheckout,
        language: getStripeLanguage(),
      })

      onOpen?.()
    },
    [currency],
  )

  const returnUrl = useMemo(
    () =>
      buildReturnUrl({
        subscriptionSuccess: modalConfig.checkoutType === 'subscription',
        addonSuccess:
          modalConfig.checkoutType === 'addon' && !modalConfig.isDownloadCheckout,
        downloadSuccess: modalConfig.isDownloadCheckout,
        upsellFlow: modalConfig.upsellFlow,
        upsellSuccessStep: modalConfig.upsellSuccessStep,
        campaignArtGenerationId: modalConfig.campaignArtGenerationId,
      }),
    [
      modalConfig.checkoutType,
      modalConfig.isDownloadCheckout,
      modalConfig.upsellFlow,
      modalConfig.upsellSuccessStep,
      modalConfig.campaignArtGenerationId,
    ],
  )

  const stripeCheckoutModal = (
    <StripeCheckoutModal
      isOpen={modalConfig.isOpen}
      onClose={closeStripeCheckout}
      tier={modalConfig.tier}
      userId={userData?.uuid}
      email={userData?.email}
      returnUrl={returnUrl}
      checkoutType={modalConfig.checkoutType}
      packageCode={modalConfig.packageCode}
      promotionCode={modalConfig.promotionCode}
      offerCode={modalConfig.offerCode}
      campaignArtGenerationId={modalConfig.campaignArtGenerationId}
      language={modalConfig.language || getStripeLanguage()}
    />
  )

  return {
    openSubscriptionCheckout,
    openAddonCheckout,
    closeStripeCheckout,
    stripeCheckoutModal,
  }
}
